import React, { useMemo } from 'react';

import { Coords, Page } from 'editor/src/store/design/types';

import useSpreadTitle from 'editor/src/util/design/useSpreadTitle';
import useFabricUtils from 'editor/src/util/useFabricUtils';

import FabricTextComponent from 'editor/src/component/EditorArea/fabricComponents/FabricTextComponent';
import zIndex from 'editor/src/component/EditorArea/Spread/zIndex';
import { CanvasRotation } from 'editor/src/component/EditorArea/types';
import useCanvasRotation from 'editor/src/component/EditorArea/useCanvasRotation';

import FoldingLine from './FoldingLine';
import getCoverGuides, { CoverPanelLabel } from './getCoverGuides';

const LABEL_WIDTH = 120;
const LABEL_OFFSET = 4;

interface LabelProps {
  label: CoverPanelLabel;
  spreadName: string;
  spreadIndex: number;
  pageCoords: Coords;
  canvasRotation: CanvasRotation;
}

function CoverPanelLabelElement({ label, spreadName, spreadIndex, pageCoords, canvasRotation }: LabelProps) {
  const { mm2px } = useFabricUtils();
  const focusedContent = useMemo(() => ({ contentTitle: label.title }), [label.title]);
  const text = useSpreadTitle(spreadName, spreadIndex, focusedContent);
  const anchor = useCanvasRotation(
    canvasRotation,
    pageCoords.left + mm2px(label.x + label.width / 2),
    pageCoords.top - LABEL_OFFSET,
  );

  return (
    <FabricTextComponent
      text={text}
      left={anchor.x}
      top={anchor.y}
      width={LABEL_WIDTH}
      originX="center"
      originY="bottom"
      angle={canvasRotation.angleDeg || 0}
      fontSize={10}
      textAlign="center"
      fill="rgba(51, 51, 51, 0.5)"
      fontFamily="Roboto, Arial, sans-serif"
      zIndex={zIndex.FOLDING_LINE}
      evented={false}
      selectable={false}
    />
  );
}

interface Props {
  page: Page;
  spreadIndex: number;
  pageCoords: Coords;
  canvasRotation: CanvasRotation;
}

// Editor-only guides for a book cover: fold lines on the spine edges and a label above each
// cover panel. They are canvas overlays, never part of the design, so they do not print.
function CoverGuides({ page, spreadIndex, pageCoords, canvasRotation }: Props) {
  const guides = useMemo(() => getCoverGuides(page), [page]);
  if (!guides) {
    return null;
  }

  return (
    <>
      {guides.foldingLines.map((line, index) => (
        <FoldingLine key={`fold-${index}`} element={line} pageCoords={pageCoords} canvasRotation={canvasRotation} />
      ))}
      {guides.panelLabels.map((label, index) => (
        <CoverPanelLabelElement
          key={`label-${index}`}
          label={label}
          spreadName={page.spread}
          spreadIndex={spreadIndex}
          pageCoords={pageCoords}
          canvasRotation={canvasRotation}
        />
      ))}
    </>
  );
}

export default React.memo(CoverGuides);
